/**
 * Extracts chart/image payloads from chat SSE events and assistant text.
 */

const IMAGE_MARKDOWN_RE = /!\[[^\]]*\]\((data:image\/[^)]+|https?:\/\/[^)\s]+)\)/gi;
const DATA_IMAGE_RE = /(data:image\/[a-z0-9+.-]+;base64,[A-Za-z0-9+/=\s]+)/gi;

export const normalizeImageSrc = (value) => {
  if (value == null) return null;

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('data:image/')) {
    return trimmed.replace(/\s+/g, '');
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  if (/^[A-Za-z0-9+/=\s]+$/.test(trimmed) && trimmed.replace(/\s/g, '').length > 80) {
    return `data:image/png;base64,${trimmed.replace(/\s/g, '')}`;
  }

  return null;
};

export const extractImagesFromText = (text) => {
  if (!text || typeof text !== 'string') return [];

  const images = [];
  const add = (candidate) => {
    const src = normalizeImageSrc(candidate);
    if (src && !images.includes(src)) {
      images.push(src);
    }
  };

  let match;
  IMAGE_MARKDOWN_RE.lastIndex = 0;
  while ((match = IMAGE_MARKDOWN_RE.exec(text)) !== null) {
    add(match[1]);
  }

  DATA_IMAGE_RE.lastIndex = 0;
  while ((match = DATA_IMAGE_RE.exec(text)) !== null) {
    add(match[1]);
  }

  return images;
};

export const extractImagesFromContentPart = (part) => {
  if (!part || typeof part !== 'object') return [];

  const images = [];
  const add = (candidate) => {
    const src = normalizeImageSrc(candidate);
    if (src) images.push(src);
  };

  const type = part.type || '';

  if (type === 'image_url') {
    add(part.image_url?.url || part.url);
  }

  if (type === 'image') {
    if (part.source?.url) {
      add(part.source.url);
    } else if (part.source?.data) {
      add(`data:${part.source.media_type || 'image/png'};base64,${part.source.data}`);
    } else if (part.url) {
      add(part.url);
    }
  }

  if (type === 'output_image' || type === 'image_file' || type === 'image_output') {
    add(part.image);
    add(part.image_file?.file_data);
    add(part.data);
    add(part.url);
  }

  if (typeof part.image_url === 'string') {
    add(part.image_url);
  }

  if (typeof part.file_data === 'string') {
    add(part.file_data);
  }

  return images;
};

const walkForImages = (value, images, depth = 0) => {
  if (value == null || depth > 8) return;

  if (typeof value === 'string') {
    extractImagesFromText(value).forEach((src) => {
      if (!images.includes(src)) images.push(src);
    });
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => walkForImages(item, images, depth + 1));
    return;
  }

  if (typeof value !== 'object') return;

  extractImagesFromContentPart(value).forEach((src) => {
    if (!images.includes(src)) images.push(src);
  });

  Object.values(value).forEach((item) => walkForImages(item, images, depth + 1));
};

export const extractImagesFromPayload = (payload) => {
  const images = [];
  walkForImages(payload, images);
  return images;
};

export const mergeUniqueImages = (...lists) => {
  const merged = [];
  lists.flat().forEach((src) => {
    const normalized = normalizeImageSrc(src);
    if (normalized && !merged.includes(normalized)) {
      merged.push(normalized);
    }
  });
  return merged;
};

/**
 * Removes embedded image payloads from user-facing markdown text.
 * Images are rendered separately in the chat UI.
 */
export const stripEmbeddedImagesFromText = (text) => {
  if (!text || typeof text !== 'string') return '';

  return text
    .replace(IMAGE_MARKDOWN_RE, '')
    .replace(DATA_IMAGE_RE, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
